import { useEffect, useState } from "react"
import styled from "styled-components"

import { useLazyGetAlertTypesQuery } from "store/api/widgetsApi"
import { ApiTypes } from "types"

import WidgetsLayout from "layouts/WidgetsLayout"
import { RainbowChart } from "components/common/RainbowChart"

const CHART_CONFIG = [
  { key: "efficiency_issue" as const, label: "System Efficiency", color: "#f9a8d4" },
  { key: "air_quality" as const, label: "Air Quality", color: "#facc15" },
  { key: "fan_malfunction" as const, label: "Fan Malfunction", color: "#60a5fa" },
  { key: "ambient_sensor_malfunction" as const, label: "Ambient Sensor Malfunction", color: "#86efac" },
  { key: "humidity_sensor_malfunction" as const, label: "Humidity Sensor Malfunction", color: "#2dd4bf" },
  { key: "temperature_sensor_malfunction" as const, label: "Temperature Sensor Malfunction", color: "#7dd3fc" },
] as const

const dummyData: ApiTypes.Res.AlertTypesWidget = {
  air_quality: 4,
  ambient_sensor_malfunction: 12,
  fan_malfunction: 5,
  humidity_sensor_malfunction: 3,
  temperature_sensor_malfunction: 5,
  efficiency_issue: 46,
  devices_with_active_alerts: 75,
  temperature: 0,
}

const initialState: ApiTypes.Res.AlertTypesWidget = {
  air_quality: 0,
  ambient_sensor_malfunction: 0,
  fan_malfunction: 0,
  humidity_sensor_malfunction: 0,
  temperature_sensor_malfunction: 0,
  efficiency_issue: 0,
  devices_with_active_alerts: 0,
  temperature: 0,
}

const ChartWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 50px;
  position: relative;
  margin-top: 25px;
`

const Chart = styled.div`
  position: absolute;
  bottom: -126px;
  left: 50%;
  transform: translateX(-50%);
`

const Text = styled.span`
  font-size: 12px;
  color: #64748b;
  text-transform: uppercase;
  width: 85px;
  text-align: center;
`

const WidgetMainValue = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 5px 0 10px;
`

const WidgetValueWrapper = styled.div`
  margin-top: 4px;
  font-size: 24px;
  font-weight: 500;
  line-height: 1.5;
  color: #0f172a;
`

const LegendFooter = styled.footer`
  border-top: 1px solid #f1f5f9;
  padding-top: 16px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 4px 24px;
`

const LegendItem = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #64748b;

  &::before {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    display: inline-flex;
    flex-shrink: 0;
    content: "";
    background-color: ${({ $color }) => $color};
  }
`

const LegendValue = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #0f172a;
`

interface WidgetMonitoringAlertsProps {
  dateRange: [string, string]
  unsupported?: boolean
}

export const WidgetMonitoringAlerts = ({ dateRange, unsupported = false }: WidgetMonitoringAlertsProps) => {
  const [triggerGetAlertTypes, { isFetching }] = useLazyGetAlertTypesQuery()
  const [widgetData, setWidgetData] = useState<ApiTypes.Res.AlertTypesWidget>(initialState)

  useEffect(() => {
    if (unsupported) {
      setWidgetData(dummyData)
      return
    }

    const fetchData = async () => {
      const res = await triggerGetAlertTypes({ date_start: dateRange[0], date_end: dateRange[1] })
      if (res?.data?.data) {
        setWidgetData(res.data.data)
      }
    }

    fetchData()
  }, [unsupported, dateRange, triggerGetAlertTypes])

  const data = unsupported ? dummyData : widgetData
  const chartItems = CHART_CONFIG.map(c => ({ ...c, value: data[c.key] ?? 0 }))
  const total = data.devices_with_active_alerts ?? chartItems.reduce((sum, item) => sum + item.value, 0)

  return (
    <WidgetsLayout title="Monitoring Alerts" isFetching={!unsupported && isFetching}>
      <ChartWrapper>
        <Chart>
          <RainbowChart
            values={chartItems.map(d => d.value)}
            colors={chartItems.map(d => d.color)}
            options={{ cutout: "60%", rotation: -95, circumference: 190 }}
          />
        </Chart>
      </ChartWrapper>
      <WidgetMainValue>
        <Text>Thermostats with alerts</Text>
        <WidgetValueWrapper>{total}</WidgetValueWrapper>
      </WidgetMainValue>
      <LegendFooter>
        {chartItems.map(item => (
          <LegendItem key={item.key} $color={item.color}>
            <span>{item.label}</span>
            <LegendValue>{item.value}</LegendValue>
          </LegendItem>
        ))}
      </LegendFooter>
    </WidgetsLayout>
  )
}
